<script setup>
import IcoInfo from '@/Images/IcoInfo.vue';
import IcoSpinner from '@/Images/IcoSpinner.vue';
import InputError from '@/Components/InputError.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import { onMounted, toRef } from 'vue';
import { childSubscription } from './Welcome';

import { trans, transChoice } from 'laravel-vue-i18n';

const props = defineProps({
    debug: { type: Boolean, default: false },
    //
    partner: { type: String },
    entitlement_id: { type: String },
    entitlement_lookup_key: { type: String },
    //
    offering: { type: Object, },
    success_url: { type: String },
    cancel_url: { type: String },
    //
    coupon: { type: Object },
    //
    has_local_coupon: { type: Boolean, default: false },
    has_stripe_coupon: { type: Boolean, default: false },
    payment_label: { type: String, default: "Ce coupon permet une réduction de ${coupon_off}" },
    payment_labels: { type: Object },
    //
    route_submit: { type: String },
    updateSubscription: { type: Function },
    reloadOffering: { type: Function },
    subscription: { type: Object },
});

import {
    offering,
    subscription,
    form,
    has_local_coupon,
    has_stripe_coupon,
    payment_label,
    payment_labels,
    route_submit,
    currentCoupon,
    currentCouponCode,
    resetCoupon,
    checkCoupon,
    preCheckCoupon,
    submit,
    emit
} from './CouponForm';

subscription.value = toRef(props.subscription).value;
const _emit = defineEmits(['updateSubscription', 'reloadOffering']);

const init = () => {
    offering.value = props.offering;
    subscription.value = props.subscription;
    form.partner = props.partner;
    form.entitlement_id = props.entitlement_id;
    form.entitlement_lookup_key = props.entitlement_lookup_key;
    form.success_url = props.success_url.replace('session_id={CHECKOUT_SESSION_ID}', '');;
    form.cancel_url = props.cancel_url;
    has_local_coupon.value = props.has_local_coupon;
    has_stripe_coupon.value = props.has_stripe_coupon;
    payment_label.value = props.payment_label;
    payment_labels.value = props.payment_labels;
    route_submit.value = props.route_submit;
    emit.value = _emit;
    resetCoupon();
}

onMounted(() => {
    init()
});

defineExpose({
    currentCoupon,
    resetCoupon,
    checkCoupon,
    preCheckCoupon,
    submit,
    form
});
</script>
<template>
    <template v-if="debug">
        <pre class="text-left text-orange-700">currentCoupon: {{ currentCoupon }}</pre>
        <pre class="text-left text-green-700">subscription.app_user_coupon: {{ subscription.app_user_coupon }}</pre>
        <pre class="text-left text-green-700">subscription.app_user_coupon_stripe: {{ subscription.app_user_coupon_stripe }}</pre>
        <pre class="text-left text-orange-700">form: {{ form }}</pre>
        <pre class="text-left text-red-700">form.errors: {{ form.errors }}</pre>
    </template>

    <form id="coupon-box" v-show="has_local_coupon || currentCoupon.status != 'success'" @submit.prevent="preCheckCoupon()">
        <div>

            <div class="relative w-64 mx-auto">
                <div class="absolute inset-y-0 right-0 flex items-center px-2">
                    <label @click="form.coupon = '', form.clearErrors(), resetCoupon()" class="px-2 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 font-mono cursor-pointer" for="coupon">
                        <!-- <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                            stroke="currentColor" class="w-6 h-6">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg> -->
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </label>
                </div>
                <TextInput id="coupon" name="coupon" type="text" v-model="form.coupon" maxlength="9" class="mt-1 block w-full uppercase tracking-[0.2em] text-center text-xl font-bold" :class="{ 'text-gray-400': form.processing || currentCoupon.processing }" :disabled="form.processing || currentCoupon.processing" />
            </div>

            <Transition v-if="form.errors.coupon_is_empty">
                <InputError class="mt-2" :message="trans('Coupon absent')" />
            </Transition>
            <Transition v-else-if="form.errors.coupon_doesnt_exists">
                <InputError class="mt-2" :message="trans('Ce coupon n\'existe pas')" />
            </Transition>
            <Transition v-else-if="form.errors.coupon_is_already_used">
                <InputError class="mt-2" :message="trans('Coupon déjà utilisé')" />
            </Transition>
            <Transition v-else-if="form.errors.coupon_wrong_partner">
                <InputError class="mt-2" :message="trans('Mauvais coupon')" />
            </Transition>
            <Transition v-else-if="form.errors.coupon_wrong_partner_2">
                <InputError class="mt-2" :message="trans('Mauvais coupon')" />
            </Transition>
            <Transition v-else-if="form.errors.coupon_not_valid">
                <InputError class="mt-2" :message="trans('Mauvais coupon')" />
            </Transition>
            <Transition v-else>
                <InputError class="mt-2" :message="form.errors.coupon" />
            </Transition>

            <Transition v-if="childSubscription && childSubscription.childCheckout">
                <InputError v-if="subscription.app_user_id && childSubscription.childCheckout.form.errors.entitlement_already_subscribed" class="mt-2" :message="trans('Vous avez déjà un abonnement en cours pour cette période.')" />
            </Transition>

        </div>

        <div class="flex gap-4 justify-center mt-6 mb-2">

            <PrimaryButton v-show="!currentCouponCode || has_stripe_coupon || has_local_coupon" type="submit" :class="{ 'opacity-25': form.processing || currentCoupon.processing }" :disabled="form.processing || currentCoupon.processing">
                <!-- <IcoSpinner v-show="form.processing || currentCoupon.processing" /> -->
                {{ trans('Vérifier') }}
            </PrimaryButton>
            <!-- <SecondaryButton v-show="!!currentCouponCode" @click="setAppUserCoupon()" type="button" class="ml-2"
                :class="{ 'opacity-25': form.processing || currentCoupon.processing || !currentCouponCode }">
                Valider
            </SecondaryButton> -->
        </div>
    </form>

    <div v-if="!form.errors" class="mt-4 mx-4 p-4 flex items-center text-left justify-center border bg-white/40 dark:bg-[#1D2F33]/40 border-white/80 dark:border-gray-500 dark:bg-gray-700 max-h-96 overflow-auto my-4 font-semibold">
        <IcoSpinner v-show="form.processing || currentCoupon.processing" />
        <IcoInfo v-show="!form.processing && !currentCoupon.processing" />
        <div v-html="resultMsg"></div>
    </div>
</template>